import React, { useState, useEffect } from 'react';
import { Button, Modal, message, Input, Descriptions, Row, Col } from 'antd';
import { BugOutlined } from '@ant-design/icons';
import config from '../config';
import { API } from 'aws-amplify';
import dayjs from '../dayjs';
import { useAppContext } from '../libs/contextLib';
import { s3Upload } from '../libs/awsLib';
import AdmZip from 'adm-zip';

const { TextArea } = Input;

// Set this to true to enable mock recording mode
const MOCK_RECORDING = false;
// Mock recording URL for testing
const MOCK_RECORDING_URL = 'https://example.com/mock-recording';

// Helper function to safely stringify objects with circular references
const safeStringify = (obj) => {
  const seen = new WeakSet();
  return JSON.stringify(obj, (key, value) => {
    // Handle circular references
    if (typeof value === 'object' && value !== null) {
      if (seen.has(value)) {
        return '[Circular Reference]';
      }
      seen.add(value);
    }
    return value;
  });
};

// Helper function to get system information
const getSystemInfo = () => {
  const info = {
    os: navigator.platform,
    browser: navigator.userAgent,
    screenResolution: `${window.screen.width}x${window.screen.height}`,
    colorDepth: window.screen.colorDepth,
    language: navigator.language,
    memory: navigator.deviceMemory ? `${navigator.deviceMemory}GB` : 'Unknown',
    connection: navigator.connection ? {
      type: navigator.connection.effectiveType,
      downlink: `${navigator.connection.downlink}Mbps`,
      rtt: `${navigator.connection.rtt}ms`
    } : 'Unknown',
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    cookiesEnabled: navigator.cookieEnabled,
    doNotTrack: navigator.doNotTrack,
    touchPoints: navigator.maxTouchPoints || 0
  };
  
  // Add memory usage information if available
  if (window.performance && window.performance.memory) {
    // Chrome-specific memory info
    info.memoryUsage = {
      totalJSHeapSize: formatBytes(window.performance.memory.totalJSHeapSize),
      usedJSHeapSize: formatBytes(window.performance.memory.usedJSHeapSize),
      jsHeapSizeLimit: formatBytes(window.performance.memory.jsHeapSizeLimit)
    };
  } else if (window.performance && window.performance.getEntriesByType) {
    // Try to get memory info from performance entries
    try {
      const memoryEntries = window.performance.getEntriesByType('memory');
      if (memoryEntries && memoryEntries.length > 0) {
        info.memoryUsage = {
          totalJSHeapSize: formatBytes(memoryEntries[0].totalJSHeapSize),
          usedJSHeapSize: formatBytes(memoryEntries[0].usedJSHeapSize),
          jsHeapSizeLimit: formatBytes(memoryEntries[0].jsHeapSizeLimit)
        };
      }
    } catch (e) {
      console.error('Error getting memory info:', e);
    }
  }
  
  return info;
};

// Helper function to format bytes to human-readable format
const formatBytes = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

// Helper to serialize breadcrumbs (string or React element)
const serializeBreadcrumb = (bc) => {
  if (typeof bc === 'string') return bc;
  if (bc && bc.props && bc.props.children) {
    if (Array.isArray(bc.props.children)) {
      return bc.props.children.map(serializeBreadcrumb).join('');
    }
    return serializeBreadcrumb(bc.props.children);
  }
  return '';
};

const ReportIssueButton = ({user, operator, breadcrumbs, isAdmin}) => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingUrl, setRecordingUrl] = useState('');
  const [description, setDescription] = useState('');
  const [title, setTitle] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();
  const [didRecord, setDidRecord] = useState(false);
  const [consoleLogs, setConsoleLogs] = useState([]);
  const [logFileUrl, setLogFileUrl] = useState('');
  const [mockRecordingInProgress, setMockRecordingInProgress] = useState(false);
  const [systemInfo, setSystemInfo] = useState(null);

  // Detect if running on localhost
  const isLocalhost = typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
  const isAdminOrDev = isAdmin || isLocalhost;

  // Collect system information when component mounts
  useEffect(() => {
    setSystemInfo(getSystemInfo());
  }, []);

  // Override console methods to capture logs
  useEffect(() => {
    if (isRecording || mockRecordingInProgress) {
      // Store original console methods
      const originalConsoleLog = console.log;
      const originalConsoleError = console.error;
      const originalConsoleWarn = console.warn;
      const originalConsoleInfo = console.info;
      
      // Override console methods to capture logs
      console.log = (...args) => {
        originalConsoleLog.apply(console, args);
        // setConsoleLogs(prev => [...prev, { 
        //   type: 'log', 
        //   timestamp: new Date().toISOString(), 
        //   message: args.map(arg => {
        //     if (typeof arg === 'object') {
        //       try {
        //         return safeStringify(arg);
        //       } catch (e) {
        //         return '[Object that could not be stringified]';
        //       }
        //     }
        //     return String(arg);
        //   }).join(' ')
        // }]);
      };
      
      console.error = (...args) => {
        originalConsoleError.apply(console, args);
        setConsoleLogs(prev => [...prev, { 
          type: 'error', 
          timestamp: new Date().toISOString(), 
          message: args.map(arg => {
            if (typeof arg === 'object') {
              try {
                return safeStringify(arg);
              } catch (e) {
                return '[Object that could not be stringified]';
              }
            }
            return String(arg);
          }).join(' ')
        }]);
      };
      
      console.warn = (...args) => {
        originalConsoleWarn.apply(console, args);
        // setConsoleLogs(prev => [...prev, { 
        //   type: 'warn', 
        //   timestamp: new Date().toISOString(), 
        //   message: args.map(arg => {
        //     if (typeof arg === 'object') {
        //       try {
        //         return safeStringify(arg);
        //       } catch (e) {
        //         return '[Object that could not be stringified]';
        //       }
        //     }
        //     return String(arg);
        //   }).join(' ')
        // }]);
      };
      
      console.info = (...args) => {
        originalConsoleInfo.apply(console, args);
        // setConsoleLogs(prev => [...prev, { 
        //   type: 'info', 
        //   timestamp: new Date().toISOString(), 
        //   message: args.map(arg => {
        //     if (typeof arg === 'object') {
        //       try {
        //         return safeStringify(arg);
        //       } catch (e) {
        //         return '[Object that could not be stringified]';
        //       }
        //     }
        //     return String(arg);
        //   }).join(' ')
        // }]);
      };
      
      // Restore original console methods when component unmounts or recording stops
      return () => {
        console.log = originalConsoleLog;
        console.error = originalConsoleError;
        console.warn = originalConsoleWarn;
        console.info = originalConsoleInfo;
      };
    }
  }, [isRecording, mockRecordingInProgress]);

  // Handle mock recording completion when modal is opened
  useEffect(() => {
    if (MOCK_RECORDING && mockRecordingInProgress && isModalVisible) {
      // Simulate recording completion when modal is opened
      setMockRecordingInProgress(false);
      setRecordingUrl(MOCK_RECORDING_URL);
      messageApi.success('Mock recording completed successfully!');
    }
  }, [isModalVisible, mockRecordingInProgress, messageApi]);

  const startRecording = () => {
    try {
      if (MOCK_RECORDING) {
        // Mock recording mode
        setIsRecording(false);
        setMockRecordingInProgress(true);
        setDidRecord(true);
        // Clear previous logs when starting a new recording
        setConsoleLogs([]);
        setLogFileUrl('');
        
        // Close the modal to simulate starting the recording
        setIsModalVisible(false);
        messageApi.info('Mock recording started. Please reproduce the issue.');
        
        // Add some mock console logs
        console.log('Mock recording started');
        console.info('This is a mock recording for testing purposes');
        console.warn('Some warning message');
        console.error('Some error message');
        
        return;
      }
      
      // Real recording mode
      setIsRecording(true);
      setDidRecord(true);
      // Clear previous logs when starting a new recording
      setConsoleLogs([]);
      setLogFileUrl('');

      const options = {
        apiKey: config.zight.apiKey,
        onCompleted: function({ shareUrl, thumbnailUrl, embedUrl }) {
          // Store the share URL
          setRecordingUrl(shareUrl);
          setIsRecording(false);
          messageApi.success('Recording completed successfully!');
          // Reopen the modal to show the recording URL
          setIsModalVisible(true);
        },
        onStatusChange: function(newStatus) {
          if (newStatus === "recording") {
            // Recording has started - close the modal when Zight starts recording
            setIsModalVisible(false);
            messageApi.info('Recording started. Please reproduce the issue.');
          } else if (newStatus === "stopped") {
            // Recording has been stopped
            setIsRecording(false);
          }
        },
        onError: function(error) {
          messageApi.error('Recording failed: ' + error.message);
          console.error('Recording failed:', error);
          setIsRecording(false);
        }
      };
      
      // Start the Zight recorder
      window.CloudAppRecorder.start(options);
    } catch (error) {
      messageApi.error('Failed to start recording: ' + error.message);
      console.error('Failed to start recording:', error);
      setIsRecording(false);
    }
  };

  const uploadLogsToS3 = async () => {
    if (consoleLogs.length === 0) return null;
    
    try {
      // Format logs as a readable text file
      const logContent = consoleLogs.map(log => 
        `[${log.timestamp}] [${log.type.toUpperCase()}] ${log.message}`
      ).join('\n');
      
      // Create a file with the logs
      const fileName = `bug-report-logs-${dayjs().format('YYYY-MM-DD-HH-mm-ss')}.txt`;
      
      // Create a proper S3 key path
      const s3Key = `${operator?.operatorId || 'public'}/bug-reports/${dayjs().format('YYYY-MM-DD')}/${fileName}`;
      
      // Create a zip file using adm-zip
      const zip = new AdmZip();
      zip.addFile(fileName, Buffer.from(logContent));
      
      // Get the zip buffer
      const zipBuffer = zip.toBuffer();
      
      // Create a File object from the zip buffer
      const file = new File([zipBuffer], `${fileName}.zip`, { type: 'application/zip' });
      
      // Upload to S3 using the s3Upload function with proper parameters
      const fileKey = await s3Upload(file, s3Key, {
        level: 'public',
        progressCallback: (progress) => {
          console.log(`Upload progress: ${progress.loaded}/${progress.total}`);
        }
      });
      
      // Return the S3 URL
      return `https://${config.s3.BUCKET}.s3.ap-southeast-2.amazonaws.com/public/${fileKey}`;
    } catch (error) {
      console.error('Failed to upload logs to S3:', error);
      return null;
    }
  };

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);
      const currentUrl = window.location.href;
      const breadcrumbsString = (breadcrumbs && breadcrumbs.length)
        ? breadcrumbs.map(serializeBreadcrumb).join(' > ')
        : 'None';
      
      // Admin/dev mode: skip recording/logs, just submit title, description, system info
      if (isAdminOrDev) {
        // Create JIRA ticket with proper structure according to Atlassian API
        const ticketData = {
          fields: {
            project: {
              key: config.jira.projectKey
            },
            summary: title,
            description: {
              type: "doc",
              version: 1,
              content: [
                {
                  type: "paragraph",
                  content: [
                    { type: "text", text: `User: ${user?.attributes?.email || 'Unknown User'}` }
                  ]
                },
                {
                  type: "paragraph",
                  content: [
                    { type: "text", text: `Operator: ${operator?.operatorName || 'Unknown Operator'}` }
                  ]
                },
                {
                  type: "paragraph",
                  content: [
                    { type: "text", text: `Breadcrumbs: ${breadcrumbsString}` }
                  ]
                },
                {
                  type: "paragraph",
                  content: [
                    { type: "text", text: `URL: ${currentUrl}` }
                  ]
                },
                {
                  type: "paragraph",
                  content: [
                    { type: "text", text: `Description: ${description}` }
                  ]
                },
                {
                  type: "paragraph",
                  content: [
                    { type: "text", text: "System Information:" }
                  ]
                },
                {
                  type: "paragraph",
                  content: [
                    { type: "text", text: `OS: ${systemInfo?.os || 'Unknown'}` }
                  ]
                },
                {
                  type: "paragraph",
                  content: [
                    { type: "text", text: `Browser: ${systemInfo?.browser || 'Unknown'}` }
                  ]
                },
                {
                  type: "paragraph",
                  content: [
                    { type: "text", text: `Screen Resolution: ${systemInfo?.screenResolution || 'Unknown'}` }
                  ]
                },
                {
                  type: "paragraph",
                  content: [
                    { type: "text", text: `Memory: ${systemInfo?.memory || 'Unknown'}` }
                  ]
                },
                ...(systemInfo?.memoryUsage ? [
                  {
                    type: "paragraph",
                    content: [
                      { type: "text", text: `Memory Usage: ${systemInfo.memoryUsage.usedJSHeapSize} / ${systemInfo.memoryUsage.totalJSHeapSize} (Limit: ${systemInfo.memoryUsage.jsHeapSizeLimit})` }
                    ]
                  }
                ] : []),
                {
                  type: "paragraph",
                  content: [
                    { type: "text", text: `Network: ${typeof systemInfo?.connection === 'object' ? `${systemInfo.connection.type} (${systemInfo.connection.downlink} downlink, ${systemInfo.connection.rtt} RTT)` : 'Unknown'}` }
                  ]
                },
                {
                  type: "paragraph",
                  content: [
                    { type: "text", text: `Time Zone: ${systemInfo?.timeZone || 'Unknown'}` }
                  ]
                }
              ]
            },
            issuetype: {
              name: "Bug"
            }
          }
        };
        if (!MOCK_RECORDING) {
          const response = await API.post('third_party', '/jira/create-issue', {
            body: ticketData
          });
          messageApi.success('Issue reported successfully!');
          setIsModalVisible(false);
          setDescription('');
          setTitle('');
          setRecordingUrl('');
        } else {
          messageApi.success('Mock issue reported successfully!');
          setIsModalVisible(false);
          setDescription('');
          setTitle('');
          setRecordingUrl('');
        }
        return;
      }
      
      // Upload logs to S3 if there are any
      let logFileUrl = null;
      if (consoleLogs.length > 0) {
        messageApi.info('Uploading console logs...');
        if (!MOCK_RECORDING) {
          logFileUrl = await uploadLogsToS3();
          if (logFileUrl) {
            messageApi.success('Console logs uploaded successfully');
          }
        } else {
          console.log('Mocked', consoleLogs)
        }
      }
      
      // Create JIRA ticket with proper structure according to Atlassian API
      const ticketData = {
        fields: {
          project: {
            key: config.jira.projectKey
          },
          summary: `Bug Report from ${user?.attributes?.email || 'Unknown User'} - [${dayjs().format('YYYY-MM-DD HH:mm:ss')}]`,
          description: {
            type: "doc",
            version: 1,
            content: [
              {
                type: "paragraph",
                content: [
                  {
                    type: "text",
                    text: `User: ${user?.attributes?.email || 'Unknown User'}`
                  }
                ]
              },
              {
                type: "paragraph",
                content: [
                  {
                    type: "text",
                    text: `Operator: ${operator?.operatorName || 'Unknown Operator'}`
                  }
                ]
              },
              {
                type: "paragraph",
                content: [
                  {
                    type: "text",
                    text: `Breadcrumbs: ${breadcrumbsString}`
                  }
                ]
              },
              {
                type: "paragraph",
                content: [
                  {
                    type: "text",
                    text: `URL: ${currentUrl}`
                  }
                ]
              },
              {
                type: "paragraph",
                content: [
                  {
                    type: "text",
                    text: `Description: ${description}`
                  }
                ]
              },
              {
                type: "paragraph",
                content: [
                  {
                    type: "text",
                    text: "Recording: "
                  },
                  {
                    type: "inlineCard",
                    attrs: {
                      url: recordingUrl
                    }
                  }
                ]
              },
              ...(logFileUrl ? [{
                type: "paragraph",
                content: [
                  {
                    type: "text",
                    text: "Console Logs: "
                  },
                  {
                    type: "inlineCard",
                    attrs: {
                      url: logFileUrl
                    }
                  }
                ]
              }] : []),
              {
                type: "paragraph",
                content: [
                  {
                    type: "text",
                    text: "System Information:"
                  }
                ]
              },
              {
                type: "paragraph",
                content: [
                  {
                    type: "text",
                    text: `OS: ${systemInfo?.os || 'Unknown'}`
                  }
                ]
              },
              {
                type: "paragraph",
                content: [
                  {
                    type: "text",
                    text: `Browser: ${systemInfo?.browser || 'Unknown'}`
                  }
                ]
              },
              {
                type: "paragraph",
                content: [
                  {
                    type: "text",
                    text: `Screen Resolution: ${systemInfo?.screenResolution || 'Unknown'}`
                  }
                ]
              },
              {
                type: "paragraph",
                content: [
                  {
                    type: "text",
                    text: `Memory: ${systemInfo?.memory || 'Unknown'}`
                  }
                ]
              },
              ...(systemInfo?.memoryUsage ? [
                {
                  type: "paragraph",
                  content: [
                    {
                      type: "text",
                      text: `Memory Usage: ${systemInfo.memoryUsage.usedJSHeapSize} / ${systemInfo.memoryUsage.totalJSHeapSize} (Limit: ${systemInfo.memoryUsage.jsHeapSizeLimit})`
                    }
                  ]
                }
              ] : []),
              {
                type: "paragraph",
                content: [
                  {
                    type: "text",
                    text: `Network: ${typeof systemInfo?.connection === 'object' ? 
                      `${systemInfo.connection.type} (${systemInfo.connection.downlink} downlink, ${systemInfo.connection.rtt} RTT)` : 
                      'Unknown'}`
                  }
                ]
              },
              {
                type: "paragraph",
                content: [
                  {
                    type: "text",
                    text: `Time Zone: ${systemInfo?.timeZone || 'Unknown'}`
                  }
                ]
              }
            ]
          },
          issuetype: {
            name: "Bug"
          }
        }
      };

      if (!MOCK_RECORDING) {
        const response = await API.post('third_party', '/jira/create-issue', {
          body: ticketData
        });

        messageApi.success('Issue reported successfully!', response.key);
        setIsModalVisible(false);
        setDescription('');
        setRecordingUrl('');
        // reload the page (to remove zight recorder remanants)
        if (didRecord && !MOCK_RECORDING) {
          window.location.reload();
        }
      } else {
        // For mock mode, just show a success message
        messageApi.success('Mock issue reported successfully!');
        setIsModalVisible(false);
        setDescription('');
        setRecordingUrl('');
        if (didRecord && !MOCK_RECORDING) {
          window.location.reload();
        }
      }
    } catch (error) {
      messageApi.error('Failed to submit issue: ' + error.message);
      console.error('Failed to submit issue:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setIsModalVisible(false);
    setRecordingUrl('');
    setDescription('');
    // reload the page (to remove zight recorder remanants)
    if (didRecord && !MOCK_RECORDING) {
      window.location.reload();
    }
  };

  return (
    <>
      {contextHolder}
      <Button
        type="primary"
        shape="circle"
        icon={<BugOutlined />}
        size="large"
        onClick={() => setIsModalVisible(true)}
        style={{
          position: 'fixed',
          bottom: '60px',
          right: '20px',
          zIndex: 1000,
          width: '50px',
          height: '50px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
        }}
      />
      
      <Modal
        title="Report an Issue"
        open={isModalVisible}
        onCancel={handleClose}
        footer={[
          <Button key="close" onClick={handleClose}>
            Cancel
          </Button>,
          <Button
            key="submit"
            type="primary"
            loading={isSubmitting}
            onClick={handleSubmit}
            disabled={isAdminOrDev ? !title || !description : (MOCK_RECORDING ? !recordingUrl : !recordingUrl || !description)}
          >
            Submit
          </Button>
        ]}
        width={700}
      >
        {isAdminOrDev ? (
          <div style={{ marginBottom: '20px' }}>
            <p><strong>Developer/Admin Mode:</strong> Submit a ticket directly without recording. System info will be included automatically.</p>
            <Input
              placeholder="Title (required)"
              value={title}
              onChange={e => setTitle(e.target.value)}
              style={{ marginBottom: '10px' }}
              maxLength={120}
            />
            <TextArea
              rows={4}
              placeholder="Describe the issue (required)"
              value={description}
              onChange={e => setDescription(e.target.value)}
              style={{ marginBottom: '20px' }}
            />
            {systemInfo && (
              <div className='modal-info' style={{ marginBottom: '20px', padding: '15px', borderRadius: '6px',background: '#f2f2f2', border: '1px solid #e0e0e0' }}>
                <h5 className='mb-3'>System Information</h5>
                <Row gutter={[20, 20]}>
                    <Col xs={24} lg={16}>
                        <label>Browser</label>
                        <div className="w-100" style={{fontSize: '14px', lineHeight: '18px'}}>{systemInfo.browser}</div>
                    </Col>
                    <Col xs={24} lg={8}>
                        <label>OS</label>
                        <div className="w-100" style={{fontSize: '14px', lineHeight: '18px'}}>{systemInfo.os}</div>
                    </Col>
                    <Col xs={24} lg={8}>
                        <label>Screen Resolution</label>
                        <div className="w-100" style={{fontSize: '14px', lineHeight: '18px'}}>{systemInfo.screenResolution}</div>
                    </Col>
                    <Col xs={24} lg={16}>
                        <label>Network</label>
                        <div className="w-100" style={{fontSize: '14px', lineHeight: '18px'}}>{typeof systemInfo.connection === 'object' 
                        ? `${systemInfo.connection.type} (${systemInfo.connection.downlink} downlink, ${systemInfo.connection.rtt} RTT)` 
                        : 'Unknown'}</div>
                    </Col>
                    <Col xs={24} lg={8}>
                        <label>Time Zone</label>
                        <div className="w-100" style={{fontSize: '14px', lineHeight: '18px'}}>{systemInfo.timeZone}</div>
                    </Col>
                    <Col xs={24} lg={8}>
                        <label>Language</label>
                        <div className="w-100" style={{fontSize: '14px', lineHeight: '18px'}}>{systemInfo.language}</div>
                    </Col>
                    <Col xs={24} lg={8}>
                        <label>Touch Points</label>
                        <div className="w-100" style={{fontSize: '14px', lineHeight: '18px'}}>{systemInfo.touchPoints}</div>
                    </Col>
                    <Col xs={24} lg={8}>
                        <label>Memory</label>
                        <div className="w-100" style={{fontSize: '14px', lineHeight: '18px'}}>{systemInfo.memory}</div>
                    </Col>
                    {systemInfo.memoryUsage && (
                      <Col xs={24} lg={16}>
                          <label>Memory Usage</label>
                          <div className="w-100" style={{fontSize: '14px', lineHeight: '18px'}}>{systemInfo.memoryUsage.usedJSHeapSize} / {systemInfo.memoryUsage.totalJSHeapSize} (Limit: {systemInfo.memoryUsage.jsHeapSizeLimit})</div>
                      </Col>
                    )}
                  </Row>
              </div>
            )}
          </div>
        ) : (
        <>
          <div style={{ marginBottom: '20px' }}>
            <p>Follow these steps to report an issue:</p>
            <ol>
              <li>Click the "Start Recording" button below</li>
              <li>When the Zight popup appears, click "Start Recording" and follow the on-screen instructions</li>
              <li>The modal will close automatically when you start recording in the Zight popup</li>
              <li>Reproduce the actions that led to the bug</li>
              <li>Complete the recording when finished</li>
              <li>Add a description of the issue and submit</li>
            </ol>
          </div>

          <div style={{ marginBottom: '20px' }}>
            <Button
              type="primary"
              onClick={startRecording}
              loading={isRecording || mockRecordingInProgress}
              disabled={isRecording || mockRecordingInProgress}
            >
              {isRecording || mockRecordingInProgress ? 'Recording in Progress...' : 'Start Recording'}
            </Button>
            {recordingUrl && (
              <div style={{ marginTop: '10px' }}>
                <p>Recording completed! Please add a description and submit.</p>
                <a href={recordingUrl} target="_blank" rel="noopener noreferrer">
                  View Recording
                </a>
              </div>
            )}
          </div>

          {recordingUrl && (
            <TextArea
              rows={4}
              placeholder="Please add any additional information that may help us understand the issue..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              style={{ marginBottom: '20px' }}
            />
          )}

          {MOCK_RECORDING && systemInfo && (
            <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#f0f8ff', borderRadius: '4px' }}>
              <h4>System Information (Mock Mode)</h4>
              <Descriptions column={2} size="small">
                <Descriptions.Item label="OS">{systemInfo.os}</Descriptions.Item>
                <Descriptions.Item label="Browser">{systemInfo.browser}</Descriptions.Item>
                <Descriptions.Item label="Screen Resolution">{systemInfo.screenResolution}</Descriptions.Item>
                <Descriptions.Item label="Memory">{systemInfo.memory}</Descriptions.Item>
                {systemInfo.memoryUsage && (
                  <Descriptions.Item label="Memory Usage" span={2}>
                    {systemInfo.memoryUsage.usedJSHeapSize} / {systemInfo.memoryUsage.totalJSHeapSize} (Limit: {systemInfo.memoryUsage.jsHeapSizeLimit})
                  </Descriptions.Item>
                )}
                <Descriptions.Item label="Network">
                  {typeof systemInfo.connection === 'object' 
                    ? `${systemInfo.connection.type} (${systemInfo.connection.downlink} downlink, ${systemInfo.connection.rtt} RTT)` 
                    : 'Unknown'}
                </Descriptions.Item>
                <Descriptions.Item label="Time Zone">{systemInfo.timeZone}</Descriptions.Item>
                <Descriptions.Item label="Language">{systemInfo.language}</Descriptions.Item>
                <Descriptions.Item label="Touch Points">{systemInfo.touchPoints}</Descriptions.Item>
              </Descriptions>
            </div>
          )}

          <div style={{ marginTop: '20px', padding: '15px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
            <p><strong>Note:</strong> Your recording will help our team understand and fix the issue you're experiencing. 
            Please include as much detail as possible in your description.</p>
            {consoleLogs.length > 0 && (
              <p><strong>Console Logs:</strong> {consoleLogs.length} log entries have been captured and will be included with your report.</p>
            )}
            {MOCK_RECORDING && (
              <p style={{ color: 'orange' }}><strong>Mock Recording Mode:</strong> This is a simulated recording for testing purposes.</p>
            )}
          </div>
        </>
        )}
      </Modal>
    </>
  );
};

export default ReportIssueButton; 